// Copyright (c) 2026. Hardfin, Inc. All rights reserved.
// Hardfin Confidential - Restricted
import { api } from '@/api/axios-instance';
import { parseResponse } from '@/api/parse-response';
import { responseMetadataSchema } from '@/api/types';
import { IsoCurrencyCode } from '@/currency/constants/iso';
import {
  type DepreciationScheduleReportEstimate,
  depreciationScheduleReportEstimateSchema,
} from '@/report/api/types';
import { DateTime } from 'luxon';
import queryString from 'query-string';
import { z } from 'zod';

const depreciationScheduleEstimateResponseSchema = z
  .object({
    data: depreciationScheduleReportEstimateSchema,
    metadata: responseMetadataSchema,
  })
  .transform(({ data }) => data);

interface GetDepreciationScheduleReportEstimateParameters {
  startDate: DateTime;
  endDate: DateTime;
  itemKeys?: string[];
  locationKeys?: string[];
  currency?: IsoCurrencyCode;
}

function getDepreciationScheduleReportEstimateQueryParams(
  parameters: GetDepreciationScheduleReportEstimateParameters
) {
  return {
    startDate: parameters.startDate.toISODate(),
    endDate: parameters.endDate.toISODate(),
    itemKeys:
      parameters.itemKeys && parameters.itemKeys.length > 0
        ? parameters.itemKeys
        : undefined,
    locationKeys:
      parameters.locationKeys && parameters.locationKeys.length > 0
        ? parameters.locationKeys
        : undefined,
    currency: parameters.currency,
  };
}

export async function getDepreciationScheduleReportEstimate(
  parameters: GetDepreciationScheduleReportEstimateParameters
): Promise<DepreciationScheduleReportEstimate> {
  const path = '/v1/report/asset/depreciation-schedule/estimate';
  const response = await api.get(path, {
    paramsSerializer: (params) => queryString.stringify(params),
    params: getDepreciationScheduleReportEstimateQueryParams(parameters),
  });

  return parseResponse(
    depreciationScheduleEstimateResponseSchema,
    response.data,
    {
      schemaName: 'DepreciationScheduleReportEstimate',
      endpoint: path,
    }
  );
}
